<template>
  <div class="rich-text-editor">
    <!-- 自定义视频上传按钮 -->
    <div class="custom-video-upload">
      <el-button type="primary" size="small" @click="openVideoUpload">
        <el-icon><Upload /></el-icon> 上传视频
      </el-button>
      <input
        ref="videoFileInput"
        type="file"
        accept="video/*"
        style="display: none;"
        @change="handleVideoFileChange"
      />
    </div>
    
    <Toolbar
      :editor="editorRef"
      :defaultConfig="toolbarConfig"
      mode="default"
      style="border-bottom: 1px solid #ccc"
    />
    <Editor
      :defaultConfig="editorConfig"
      v-model="valueHtml"
      mode="default"
      :style="{height: props.height, 'overflow-y': 'hidden'}"
      @onCreated="handleCreated"
      @onChange="handleChange"
      @onDestroyed="handleDestroyed"
    />
  </div>
</template>

<script setup>
import { ref, shallowRef, onBeforeUnmount, watch, nextTick } from 'vue'
import '@wangeditor/editor/dist/css/style.css'
import { Editor, Toolbar } from '@wangeditor/editor-for-vue'
import { ElMessage } from 'element-plus'
import { Upload } from '@element-plus/icons-vue'

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: '请输入内容...'
  },
  height: {
    type: String,
    default: '350px'
  }
})

const emit = defineEmits(['update:modelValue', 'change'])

// 编辑器实例，必须用 shallowRef
const editorRef = shallowRef()

// 内部富文本内容
const valueHtml = ref('')

// 用于文件上传的ref
const videoFileInput = ref(null)

// 监听传入的内容变化
watch(() => props.modelValue, (newVal) => {
  if (newVal !== valueHtml.value) {
    valueHtml.value = newVal
  }
}, { immediate: true })

// 监听内部内容变化
watch(() => valueHtml.value, (newVal) => {
  emit('update:modelValue', newVal)
  emit('change', newVal)
})

// 工具栏配置
const toolbarConfig = {
  excludeKeys: [
    'uploadVideo' // 禁用内置的视频上传功能
  ]
}

// 编辑器配置
const editorConfig = {
  placeholder: props.placeholder,
  // 允许保留特定样式，避免被过滤掉
  PRESERVE_STYLES: [
    'background-color', 'color', 'border', 'border-left', 'border-radius', 
    'padding', 'margin', 'font-size', 'font-weight', 'text-decoration',
    'display', 'position', 'box-shadow'
  ],
  // 允许插入的HTML标签，显式添加视频相关标签
  ALLOW_TAGS_IN_DATA_ATTR: true,   // 允许在data属性中使用标签
  EXTEND_PARSER_HTML_TAGS: {
    video: {
      isVoid: true, // 无需闭合标签
    },
    source: {
      isVoid: true,
    },
  },
  // 菜单和上传配置
  MENU_CONF: {
    uploadImage: {
      // 自定义上传，将图片上传到服务器
      customUpload(file, insertFn) {
        // 验证文件类型和大小
        if (!file.type.startsWith('image/')) {
          ElMessage.error('只能上传图片文件');
          return;
        }

        if (file.size > 2 * 1024 * 1024) {
          ElMessage.error('图片大小不能超过2MB');
          return;
        }
        
        // 创建 FormData
        const formData = new FormData()
        formData.append('file', file)
        
        // 记录当前时间用于日志
        const uploadStartTime = new Date().toISOString();
        const fileName = file.name
        const objectUrl = URL.createObjectURL(file)
        
        console.log(`[${uploadStartTime}] 开始上传图片:`, fileName);
        
        // 1. 先插入临时图片
        insertFn(objectUrl, fileName, '')
        console.log('已插入临时图片:', objectUrl)
        
        // 2. 发送上传请求
        fetch('/api/upload/image', {
          method: 'POST',
          body: formData,
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        })
          .then(res => {
            console.log('服务器响应状态:', res.status);
            if (!res.ok) {
              return res.text().then(text => {
                throw new Error(`服务器返回错误 (${res.status}): ${text}`);
              });
            }
            return res.json();
          })
          .then(res => {
            console.log('图片上传响应数据:', res);
            
            if (res.errno === 0 && res.data && res.data.url) {
              // 图片上传成功
              console.log('上传成功，服务器返回的URL:', res.data.url);
              
              // 尝试通过DOM查找并替换图片
              setTimeout(() => {
                try {
                  const images = document.querySelectorAll('.w-e-text-container img')
                  let replaced = false

                  for (let i = 0; i < images.length; i++) {
                    const img = images[i]
                    if (img.src === objectUrl) {
                      // 确保URL格式正确
                      let finalUrl = res.data.url
                      if (!finalUrl.startsWith('http') && !finalUrl.startsWith('/')) {
                        finalUrl = '/' + finalUrl
                      }

                      // 记录替换过程
                      console.log(`替换图片: 从 ${img.src} 到 ${finalUrl}`);
                      
                      // 添加数据属性，保存服务器URL，防止刷新后图片丢失
                      img.setAttribute('data-server-url', finalUrl);
                      img.setAttribute('data-blob-url', objectUrl);
                      img.setAttribute('data-original-filename', fileName);
                      
                      // 重要：替换src属性
                      img.src = finalUrl;
                      
                      // 更新编辑器内容，确保保存的是正确的URL
                      if (editorRef.value) {
                        try {
                          // 触发变更事件，确保内容更新
                          const editor = editorRef.value;
                          const html = editor.getHtml();
                          console.log('更新前的编辑器HTML:', html.substring(0, 100) + '...');
                          
                          // 强制更新HTML内容，使用正则表达式替换blob URL
                          let updatedHtml = html;
                          if (html.includes(objectUrl)) {
                            // 使用正则表达式替换所有匹配的blob URL
                            const blobUrlRegex = new RegExp(objectUrl.replace(/\//g, '\\/').replace(/\./g, '\\.'), 'g');
                            updatedHtml = html.replace(blobUrlRegex, finalUrl);
                            
                            console.log('替换后的HTML内容:', updatedHtml.substring(0, 100) + '...');
                            
                            // 设置回编辑器
                            editor.setHtml(updatedHtml);
                            
                            // 更新组件的值
                            setTimeout(() => {
                              valueHtml.value = editor.getHtml();
                              console.log('编辑器内容是否包含服务器URL:', valueHtml.value.includes(finalUrl));
                              console.log('更新后的编辑器HTML:', valueHtml.value.substring(0, 100) + '...');
                              
                              // 如果仍然有问题，使用emit强制更新父组件
                              emit('update:modelValue', valueHtml.value);
                              emit('change', valueHtml.value);
                            }, 50);
                          }
                        } catch (err) {
                          console.error('更新编辑器内容失败:', err);
                        }
                      }
                      
                      // 释放临时对象URL
                      URL.revokeObjectURL(objectUrl)
                      replaced = true
                      break
                    }
                  }

                  if (!replaced) {
                    console.warn('未找到临时图片进行替换，尝试重新插入');
                    // 如果没有找到对应的图片，重新插入一次
                    insertFn(res.data.url, fileName, '')
                    URL.revokeObjectURL(objectUrl)
                  }
                  
                  console.log('图片上传和替换完成');
                  ElMessage.success('图片上传成功');
                } catch (err) {
                  console.error('替换图片URL时出错:', err);
                  ElMessage.warning('图片已上传，但页面显示可能不正确');
                }
              }, 200); // 增加延迟，确保DOM已更新
            } else {
              console.error('上传成功但返回数据格式异常:', res);
              throw new Error(res.message || '服务器响应格式错误');
            }
          })
          .catch(err => {
            console.error('上传图片出错:', err);
            ElMessage.error('上传图片失败: ' + (err.message || '未知错误'));
            
            // 将临时图片替换为错误提示图片
            setTimeout(() => {
              try {
                const images = document.querySelectorAll('.w-e-text-container img')
                for (let i = 0; i < images.length; i++) {
                  const img = images[i]
                  if (img.src === objectUrl) {
                    URL.revokeObjectURL(objectUrl)
                    img.src = '/default-channel-icon.png'
                    img.setAttribute('title', '图片上传失败')
                    break
                  }
                }
              } catch (e) {
                console.error('替换错误图片失败:', e)
              }
            }, 200)
          });
      }
    }
  }
}

// 组件销毁时，及时销毁编辑器
onBeforeUnmount(() => {
  const editor = editorRef.value
  if (editor == null) return
  editor.destroy()
})

// 创建完毕钩子
const handleCreated = (editor) => {
  editorRef.value = editor // 记录实例

  // 配置编辑器，确保允许特定的HTML标签和样式
  try {
    if (editor) {
      // 允许保留内联样式，避免视频引用框的样式被过滤
      const styleConfig = editor.getConfig().PRESERVE_STYLES || [];
      editor.getConfig().PRESERVE_STYLES = [...new Set([
        ...styleConfig,
        'background-color', 'color', 'border', 'border-left', 'border-radius', 
        'padding', 'margin', 'font-size', 'font-weight', 'text-decoration',
        'display', 'position', 'box-shadow'
      ])];
      
      // 重要：增强编辑器配置，确保接受视频标签
      if (!editor.getConfig().EXTEND_PARSER_HTML_TAGS) {
        editor.getConfig().EXTEND_PARSER_HTML_TAGS = {};
      }
      
      // 添加视频相关标签支持
      editor.getConfig().EXTEND_PARSER_HTML_TAGS.video = {
        isVoid: true,
      };
      editor.getConfig().EXTEND_PARSER_HTML_TAGS.source = {
        isVoid: true,
      };
      
      // 允许data属性中的标签
      editor.getConfig().ALLOW_TAGS_IN_DATA_ATTR = true;
      
      console.log('编辑器配置已更新，保留样式:', editor.getConfig().PRESERVE_STYLES);
      console.log('编辑器配置已更新，允许视频标签');
    }
  } catch (e) {
    console.error('配置编辑器样式保留失败:', e);
  }

  // 初始化内容
  nextTick(() => {
    valueHtml.value = props.modelValue
    
    // 处理编辑器中可能存在的视频元素
    setTimeout(() => {
      if (editor) {
        // 查找编辑器中的所有视频元素
        const videos = document.querySelectorAll('.w-e-text-container video');
        console.log('编辑器初始化时找到视频元素数量:', videos.length);
        
        // 为每个视频元素应用样式和属性
        videos.forEach(video => {
          // 添加视频样式
          video.style.width = '100%';
          video.style.display = 'block';
          video.style.backgroundColor = '#000';
          video.style.borderRadius = '4px';
          video.style.minHeight = '300px';
          video.style.margin = '10px 0';
          
          // 确保有控制条
          video.controls = true;
          
          // 添加类名
          video.classList.add('tool-video');
          
          // 处理视频容器
          const parent = video.parentElement;
          if (parent) {
            parent.style.width = '100%';
            parent.style.margin = '10px 0';
            parent.style.position = 'relative';
            parent.style.display = 'block';
          }
          
          // 尝试加载视频
          try {
            video.load();
          } catch (e) {
            console.error('无法加载视频:', e);
          }
          
          console.log('初始化视频元素样式:', video.outerHTML);
        });
      }
    }, 300);
  })
}

// 内容变化钩子
const handleChange = (editor) => {
  valueHtml.value = editor.getHtml()
  
  // 在内容变化时，处理视频样式和链接
  setTimeout(() => {
    // 找到所有新的视频元素
    const videos = document.querySelectorAll('.w-e-text-container video:not(.tool-video)');
    if (videos.length > 0) {
      console.log('内容变化时发现新的视频元素:', videos.length);
      
      // 应用样式
      videos.forEach(video => {
        // 添加视频样式
        video.style.width = '100%';
        video.style.display = 'block';
        video.style.backgroundColor = '#000';
        video.style.borderRadius = '4px';
        video.style.minHeight = '300px';
        video.style.margin = '10px 0';
        
        // 确保有控制条
        video.controls = true;
        
        // 添加类名
        video.classList.add('tool-video');
        
        // 处理视频容器
        const parent = video.parentElement;
        if (parent) {
          parent.style.width = '100%';
          parent.style.margin = '10px 0';
          parent.style.position = 'relative';
          parent.style.display = 'block';
        }
      });
    }
    
    // 检查视频链接，确保它们仍然存在并有正确的样式
    const videoLinks = document.querySelectorAll('.w-e-text a[href*="/uploads/tool-intros/videos/"], .w-e-text a[href*=".mp4"]');
    if (videoLinks.length > 0) {
      console.log('内容变化时找到视频链接:', videoLinks.length);
      
      videoLinks.forEach(link => {
        // 确保链接有明显的样式
        link.style.color = '#409eff';
        link.style.fontWeight = 'bold';
        link.style.textDecoration = 'underline';
        
        // 查找父容器，为其添加样式
        const container = link.closest('p');
        if (container && container !== link) {
          // 避免重复应用
          if (!container.style.border || container.style.border === '') {
            container.style.padding = '8px';
            container.style.border = '1px solid #409eff';
            container.style.borderRadius = '4px';
            container.style.backgroundColor = '#f0f8ff';
            container.style.margin = '10px 0';
          }
        }
      });
    }
    
    // 确保所有视频数据标记仍然存在
    const videoDataMarks = document.querySelectorAll('.w-e-text .video-data');
    console.log('内容变化时找到视频数据标记:', videoDataMarks.length);
    
    // 如果发现编辑器过滤掉了视频数据标记，尝试重新插入
    const html = editor.getHtml();
    if (html.includes('视频附件') && html.includes('点击查看视频') && videoDataMarks.length === 0) {
      console.log('发现视频附件文本但数据标记丢失，尝试恢复数据标记');
      
      // 查找所有视频链接
      const recoveredLinks = document.querySelectorAll('.w-e-text a[href*="/uploads/tool-intros/videos/"], .w-e-text a[href*=".mp4"]');
      recoveredLinks.forEach(link => {
        const videoUrl = link.getAttribute('href');
        if (videoUrl) {
          // 检查视频链接后是否已有数据标记
          let hasDataMark = false;
          let currentNode = link;
          for (let i = 0; i < 3; i++) { // 检查后面的几个兄弟节点
            currentNode = currentNode.nextElementSibling;
            if (currentNode && currentNode.classList && currentNode.classList.contains('video-data')) {
              hasDataMark = true;
              break;
            }
          }
          
          // 如果没有数据标记，添加一个
          if (!hasDataMark) {
            try {
              const container = link.closest('p');
              if (container && container.parentNode) {
                // 创建新的数据标记
                const dataMarkElement = document.createElement('div');
                dataMarkElement.className = 'video-data';
                dataMarkElement.setAttribute('data-video-url', videoUrl);
                dataMarkElement.setAttribute('data-video-name', '');
                dataMarkElement.style.display = 'none'; // 隐藏元素
                
                // 在链接段落后插入数据标记
                container.parentNode.insertBefore(dataMarkElement, container.nextSibling);
                
                console.log('恢复了视频数据标记:', videoUrl);
              }
            } catch (e) {
              console.error('尝试恢复视频数据标记失败:', e);
            }
          }
        }
      });
    }
  }, 100);
}

// 销毁钩子
const handleDestroyed = () => {
  editorRef.value = null
}

// 打开视频上传对话框
const openVideoUpload = () => {
  if (videoFileInput.value) {
    videoFileInput.value.click();
  }
}

// 处理视频文件选择
const handleVideoFileChange = (event) => {
  const file = event.target.files[0];
  if (!file) return;
  
  // 验证文件类型和大小
  if (!file.type.startsWith('video/')) {
    ElMessage.error('只能上传视频文件');
    return;
  }

  if (file.size > 100 * 1024 * 1024) {
    ElMessage.error('视频大小不能超过100MB');
    return;
  }
  
  // 显示上传中提示
  const loadingInstance = ElMessage({
    message: '视频上传中，请耐心等待...',
    duration: 0,
    type: 'info'
  });
  
  // 记录当前时间戳，用于生成唯一ID
  const timestamp = Date.now();
  const uniqueId = `video-${timestamp}`;
  
  // 创建 FormData
  const formData = new FormData();
  formData.append('video', file);
  
  // 发送上传请求
  fetch('/api/tool-introductions/upload-video', {
    method: 'POST',
    body: formData,
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`
    }
  })
    .then(res => {
      console.log('视频上传响应状态:', res.status);
      if (!res.ok) {
        return res.text().then(text => {
          throw new Error(`服务器返回错误 (${res.status}): ${text}`);
        });
      }
      return res.json();
    })
    .then(res => {
      console.log('视频上传响应数据:', res);
      
      // 关闭加载提示
      loadingInstance.close();
      
      if (res && res.url) {
        // 上传成功
        ElMessage.success('视频上传成功');
        const videoUrl = res.url;
        
        // 确保URL格式一致
        let finalVideoUrl = videoUrl;
        if (!finalVideoUrl.startsWith('http') && !finalVideoUrl.startsWith('/')) {
          finalVideoUrl = '/' + finalVideoUrl;
        }
        
        console.log('视频最终URL:', finalVideoUrl);
        
        // 不再尝试替换临时视频，直接使用简单的方式插入视频引用
        if (editorRef.value) {
          // 创建纯文本方式的视频引用（避开富文本编辑器的限制）
          console.log('使用简单的视频引用方式');
          
          // 创建一个视频链接文本，增强样式使其更明显
          const simpleVideoRef = `
<p style="background-color: #f0f8ff; padding: 12px; border-radius: 6px; border-left: 6px solid #409eff; margin: 15px 0; position: relative; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1); animation: fadeIn 0.5s;">
  <strong style="color: #333; font-size: 16px;">📹 视频附件:</strong> ${file.name}
  <a href="${finalVideoUrl}" target="_blank" style="display:block;color:#409eff;margin-top:8px;text-decoration:underline;font-weight:bold;font-size:15px;">点击查看视频</a>
  <span style="font-size:13px;color:#666;display:block;margin-top:5px;">[视频将在查看页面播放]</span>
</p>
          `;
          
          console.log('插入视频引用:', simpleVideoRef);
          
          // 尝试插入视频引用到编辑器
          insertVideoReference(file.name, finalVideoUrl, uniqueId);
        } else {
          // 如果编辑器实例不存在，显示明确的提示
          ElMessage.warning('视频已上传成功，但无法插入编辑器。您可以手动添加视频链接。');
          console.error('编辑器实例不存在，无法插入视频引用');
        }
      } else {
        throw new Error('视频上传失败: 响应格式错误');
      }
    })
    .catch(error => {
      // 关闭加载提示
      loadingInstance.close();
      
      console.error('上传视频失败:', error);
      ElMessage.error('上传视频失败: ' + (error.message || '未知错误'));
    })
    .finally(() => {
      // 清除文件输入框的值，允许重复选择相同文件
      if (videoFileInput.value) {
        videoFileInput.value.value = '';
      }
    });
}

// 将视频引用插入逻辑提取为单独的函数，增强错误处理
const insertVideoReference = (fileName, videoUrl, uniqueId) => {
  // 尝试多种方法插入视频引用，确保至少有一种能成功
  console.log('开始插入视频引用:', videoUrl);
  
  try {
    const editor = editorRef.value;
    if (!editor) {
      console.error('编辑器实例不存在');
      ElMessage.warning('视频已上传，但找不到编辑器实例');
      return;
    }
    
    // 获取当前HTML
    const currentHtml = editor.getHtml();
    
    // 尝试插入视频元素
    console.log('尝试插入视频元素...');

    // 1. 最简洁的视频HTML结构，提高接受率
    const simpleVideoHtml = `
<p data-video="true" class="video-wrapper" style="background-color: #000;">
  <video src="${videoUrl}" controls width="100%" style="margin: 0;"></video>
</p>
<p><br></p>`;

    // 2. 如果编辑器过滤，使用纯链接作为备用方案
    const fallbackHtml = `
<p>
  <a href="${videoUrl}" target="_blank" style="color: #409eff; text-decoration: underline;">视频</a>
  <span style="display:none;" class="video-data" data-video-url="${videoUrl}" data-video-name=""></span>
</p>
<p><br></p>`;

    editor.setHtml(currentHtml + simpleVideoHtml);
    
    // 延时检查是否成功
    setTimeout(() => {
      const newHtml = editor.getHtml();
      
      // 检查是否有video元素
      const hasVideo = newHtml.includes('<video') && newHtml.includes(videoUrl);
      console.log('编辑器HTML中是否包含视频元素:', hasVideo);
      
      if (hasVideo) {
        // 成功插入了视频元素
        ElMessage.success('视频已成功嵌入');
        valueHtml.value = newHtml;
        emit('update:modelValue', newHtml);
        emit('change', newHtml);
        
        // 滚动到底部显示新内容
        const editorContainer = document.querySelector('.w-e-text-container');
        if (editorContainer) {
          editorContainer.scrollTop = editorContainer.scrollHeight;
        }
      } else {
        // 视频元素被过滤，使用备选方案(链接)
        console.log('视频元素被过滤，使用链接备选方案');
        editor.setHtml(newHtml + fallbackHtml);
        
        // 再次更新
        setTimeout(() => {
          const finalHtml = editor.getHtml();
          valueHtml.value = finalHtml;
          emit('update:modelValue', finalHtml);
          emit('change', finalHtml);
          
          // 检查链接是否存在
          const hasLink = finalHtml.includes(videoUrl);
          if (hasLink) {
            ElMessage.success('已添加视频 (视频将在浏览页面显示)');
          } else {
            // 作为紧急措施，弹出提示让用户知道视频已上传成功
            ElMessage({
              message: '视频已上传成功，可使用地址: ' + videoUrl,
              type: 'info',
              duration: 0,
              showClose: true
            });
          }
          
          // 滚动到底部显示新内容
          const editorContainer = document.querySelector('.w-e-text-container');
          if (editorContainer) {
            editorContainer.scrollTop = editorContainer.scrollHeight;
          }
        }, 100);
      }
    }, 100);
  } catch (error) {
    console.error('设置编辑器HTML失败:', error);
    
    // 直接显示链接，确保用户至少知道视频URL
    ElMessage({
      message: `视频已上传成功 (${videoUrl})`,
      type: 'success',
      duration: 0,
      showClose: true
    });
  }
}

// 附加一个顶级函数，在编辑器中直接设置一个文本内容
// 此函数可以从浏览器控制台手动调用，用于紧急情况
window.insertVideoDirectly = (fileName, videoUrl) => {
  if (editorRef.value) {
    try {
      const currentHtml = editorRef.value.getHtml();
      
      // 创建直接嵌入的视频元素，不带标题
      const videoHtml = `
<div class="video-container" style="margin: 20px 0; width: 100%; background-color: #000; border-radius: 8px; overflow: hidden; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);">
  <video src="${videoUrl}" controls style="width: 100%; display: block; min-height: 350px;"></video>
</div>`;

      editorRef.value.setHtml(currentHtml + videoHtml);
      ElMessage.success('视频已通过紧急函数嵌入');
      
      // 更新组件值
      valueHtml.value = editorRef.value.getHtml();
      emit('update:modelValue', valueHtml.value);
      emit('change', valueHtml.value);
    } catch (e) {
      console.error('紧急插入失败:', e);
      
      // 最后的备用方案 - 使用最基本的文本链接
      try {
        const simpleHtml = `
<p>
  <a href="${videoUrl}" target="_blank" style="color: #409eff;">视频附件</a>
</p>`;
        
        editorRef.value.setHtml(editorRef.value.getHtml() + simpleHtml);
        ElMessage.success('已添加视频链接');
        
        valueHtml.value = editorRef.value.getHtml();
        emit('update:modelValue', valueHtml.value);
        emit('change', valueHtml.value);
      } catch (err) {
        console.error('所有插入方法都失败:', err);
      }
    }
  } else {
    console.error('编辑器实例不存在');
  }
}
</script>

<style scoped>
.rich-text-editor {
  border: 1px solid #ccc;
  border-radius: 4px;
  z-index: 100;
  margin-bottom: 5px;
  position: relative; /* 添加相对定位，使得通知元素可以相对于编辑器定位 */
}

.custom-video-upload {
  padding: 8px 12px;
  background-color: #f5f7fa;
  border-bottom: 1px solid #ccc;
  display: flex;
  justify-content: flex-start;
}

/* 视频元素样式处理 - 使用深度选择器访问编辑器内容 */
:deep(.w-e-text-container video),
:deep(.tool-video) {
  max-width: 100% !important;
  width: 100% !important;
  height: auto !important;
  min-height: 300px;
  background-color: #000;
  margin: 10px 0;
  border-radius: 4px;
  display: block;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* 视频包装元素样式 */
:deep(.video-wrapper) {
  margin: 15px 0;
  padding: 0;
  border: 1px solid #000;
  border-radius: 6px;
  overflow: hidden;
  background: #000;
}

/* 删除视频标题样式 */
:deep(.video-title) {
  display: none !important;
}

/* 添加视频引用框样式 */
:deep(.w-e-text p[style*="border-left: 6px solid #409eff"]),
:deep(.w-e-text p[style*="border: 1px solid #409eff"]) {
  animation: highlightVideoBox 1.5s ease-in-out;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15) !important;
}

/* 视频引用框的动画效果 */
@keyframes highlightVideoBox {
  0% { background-color: rgba(64, 158, 255, 0.3); }
  50% { background-color: rgba(64, 158, 255, 0.5); }
  100% { background-color: #f0f8ff; }
}

/* 淡入动画 */
@keyframes fadeIn {
  0% { opacity: 0; transform: translateY(10px); }
  100% { opacity: 1; transform: translateY(0); }
}

/* 淡出动画 */
@keyframes fadeOut {
  0% { opacity: 1; }
  70% { opacity: 1; }
  100% { opacity: 0; }
}

:deep(.w-e-text-container .w-e-panel-container) {
  z-index: 110; /* 确保面板显示在上方 */
}
</style> 